<script lang="ts" setup>
import { useRoute } from "vue-router"
import type { MenuView } from "@/composables/use-menu-machine"
import SearchModal from "../SearchModal/SearchModal.vue"
import SVG_Logo from "../SVGs/SVG_Logo.vue"
import { getCurrentInstance } from "vue"

const { navActive } = defineProps<{
  navActive: boolean
}>()

const emit = defineEmits<{
  select: [view: MenuView]
  close: []
}>()

type NavLink = { title: string; uri: string; action?: never }
type NavButton = { title: string; uri?: never; action: MenuView }
type NavItem = NavLink | NavButton

const NAV_ITEMS: NavItem[] = [
  { title: "Critical Mass", uri: "/" },
  { title: "Index", action: "projects" },
  { title: "Programmes", action: "programmes" },
  { title: "AA Book", uri: "/aabook" },
  { title: "About", uri: "/about" },
]

const EXTERNAL_NAV_ITEMS: { title: string; uri: string }[] = [
  { title: "Virtual Tour", uri: "https://my.matterport.com/show/?m=Hh5T4zf3pXS" },
]

const route = useRoute()
const isAncestorOfRoute = (uri: string) => {
  if (uri === "/") {
    return route.path === "/"
  }
  if (route.path === "/") {
    return false
  }
  const path = route.path.replace("/students/", "/projects/")
  return path.includes(uri)
}

const { appContext } = getCurrentInstance()!
const footerLinks: { id: string; url: string; title: string }[] =
  appContext.config.globalProperties.$site.footerLinks
</script>

<template>
  <div
    class="flex flex-col h-full p-3 pt-0 transition-[opacity,transform] duration-200"
    :class="{
      'opacity-0': !navActive,
      'opacity-100 ': navActive,
    }"
  >
    <!-- <div class="text-sm italic body-text flex justify-between w-full mb-1">
      <div>Critical Mass</div>
      <div>Projects Review 2026</div>
    </div> -->
    <ul class="h-full">
      <div class="pt-1 -mx-1 mb-2 border">
        <SearchModal @close="emit('close')" />
      </div>
      <div class="leading-default">
        <li
          v-for="(item, index) in NAV_ITEMS"
          :key="index"
          class="py-1 md:hover:text-gold transition-colors lg:h4 h5"
          :class="{
            'text-gold': item.action ? false : isAncestorOfRoute(item.uri),
          }"
        >
          <div>
            <button
              v-if="item.action"
              class="w-full text-left cursor-pointer"
              @click="emit('select', item.action)"
            >
              {{ item.title }}
            </button>
            <router-link
              v-else
              :to="item.uri"
              class="w-full block"
              @click="emit('close')"
            >
              {{ item.title }}
            </router-link>
          </div>
        </li>
        <li
          v-for="item in EXTERNAL_NAV_ITEMS"
          :key="item.uri"
          class="py-1 md:hover:text-gold transition-colors lg:h4 h5"
        >
          <a
            :href="item.uri"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full block"
            @click="emit('close')"
          >
            {{ item.title }}
          </a>
        </li>
      </div>
    </ul>

    <div class="text-sm grid-cols-2 grid pb-4">
      <div v-for="{ url, title, id } in footerLinks" class="block">
        <a
          class="link"
          :href="url"
          target="_blank"
          rel="noopener noreferrer"
          :key="id"
        >
          {{ title }}
        </a>
      </div>
    </div>
    <div class="flex border-t pt-4 mt-auto">
      <a
        href="https://www.aaschool.ac.uk/"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center pointer-events-auto link"
      >
        <SVG_Logo class="w-10 -translate-y-0.5" />
        <div class="pl-2 leading-tight text-xs">
          Architectural Association<br />
          School of Architecture
        </div>
      </a>
    </div>
  </div>
</template>
